/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { plural } from '../../shared/format/plural';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { resolveSessionEmployee } from '../../auth/application/sessionEmployeeResolver';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField'
import { showSuccess } from '../../shared/ui/appToast';
import { formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { formatMoney } from '../../shared/format/public-api';
import {
  listEmployeeCommissions,
  listEmployeeCommissionDetails,
  type EmployeeCommissionSummary,
  type EmployeeCommissionDetail,
} from '../infra/commissionsRepo';
import { WORK_ORDER_STATUS_LABELS } from '../domain/workOrderStatus';

type DatePreset = 'this_month' | 'last_month' | 'all' | 'custom';

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'this_month', label: 'Este mes' },
  { key: 'last_month', label: 'Mes anterior' },
  { key: 'all', label: 'Todo' },
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
}

function presetToRange(preset: DatePreset): { from: Date | null; to: Date | null } {
  const now = new Date();
  if (preset === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return { from, to };
  }
  if (preset === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    return { from, to };
  }
  return { from: null, to: null };
}

export function CommissionsScreen() {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { canViewOthers } = useBranchPermission('WorkOrders');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const canSeeAll = canViewOthers || isOwner;

  const [summaries, setSummaries] = useState<EmployeeCommissionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);
  const [details, setDetails] = useState<EmployeeCommissionDetail[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [preset, setPreset] = useState<DatePreset>('this_month');
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);
  const [employeeFilter, setEmployeeFilter] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentBranchId) {
      setError('No hay sucursal seleccionada.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const range = preset === 'custom' ? { from: customFrom, to: customTo } : presetToRange(preset);
      let data = await listEmployeeCommissions({
        branchId: currentBranchId,
        fromIso: range.from ? range.from.toISOString() : null,
        toIso: range.to ? range.to.toISOString() : null,
      });
      if (!canSeeAll) {
        const sessionEmp = await resolveSessionEmployee();
        const empId = sessionEmp.employeeId;
        data = empId ? data.filter((r) => r.employeeId === empId) : [];
        if (empId) setEmployeeFilter(empId);
      }
      setSummaries(data);
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar comisiones.');
    } finally {
      setLoading(false);
    }
  }, [currentBranchId, preset, customFrom, customTo, canSeeAll]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const handleToggleExpand = useCallback(
    async (employeeId: string) => {
      if (expandedEmployee === employeeId) {
        setExpandedEmployee(null);
        setDetails([]);
        return;
      }
      setExpandedEmployee(employeeId);
      setDetails([]);
      setLoadingDetails(true);
      try {
        const range = preset === 'custom' ? { from: customFrom, to: customTo } : presetToRange(preset);
        const d = await listEmployeeCommissionDetails({
          employeeId,
          branchId: currentBranchId ?? '',
          fromIso: range.from ? range.from.toISOString() : null,
          toIso: range.to ? range.to.toISOString() : null,
        });
        setDetails(d);
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'Error al cargar el detalle.' });
      } finally {
        setLoadingDetails(false);
      }
    },
    [expandedEmployee, currentBranchId, preset, customFrom, customTo],
  );

  const filteredSummaries = employeeFilter
    ? summaries.filter((s) => s.employeeId === employeeFilter)
    : summaries;

  const handleExportCsv = useCallback(async () => {
    const rows: string[] = [
      'Empleado,Producto,Fecha,OT,Cantidad,Precio unit.,Subtotal,Comisión %,Monto comisión,Estado OT',
    ];
    for (const s of filteredSummaries) {
      try {
        const range = preset === 'custom' ? { from: customFrom, to: customTo } : presetToRange(preset);
        const d = await listEmployeeCommissionDetails({
          employeeId: s.employeeId,
          branchId: currentBranchId ?? '',
          fromIso: range.from ? range.from.toISOString() : null,
          toIso: range.to ? range.to.toISOString() : null,
        });
        for (const r of d) {
          const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
          rows.push([
            esc(s.employeeName),
            esc(r.productName),
            formatDate(r.createdAt),
            r.workOrderId.slice(0, 8).toUpperCase(),
            String(r.quantity),
            String(r.unitPrice),
            String(r.lineTotal),
            r.commissionPercent != null ? String(r.commissionPercent) : '',
            String(r.commissionAmount),
            esc(WORK_ORDER_STATUS_LABELS[r.workOrderStatus as keyof typeof WORK_ORDER_STATUS_LABELS] ?? r.workOrderStatus),
          ].join(','));
        }
      } catch { /* skip employee on error */ }
    }
    const csv = rows.join('\n');
    const url = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    if (Platform.OS === 'web') {
      const a = document.createElement('a');
      a.href = url;
      a.download = 'comisiones.csv';
      a.click();
    } else {
      const supported = await Linking.canOpenURL(url).catch(() => false);
      if (supported) await Linking.openURL(url);
    }
    showSuccess('Export', 'Comisiones exportadas a CSV');
  }, [filteredSummaries, preset, customFrom, customTo, currentBranchId]);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563eb" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={() => void load()}>
          <Text style={styles.retryText}>Reintentar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const grandTotal = filteredSummaries.reduce((sum, s) => sum + s.totalCommission, 0);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Comisiones por empleado</Text>
      <Text style={styles.subtitle}>Tareas asignadas en órdenes de trabajo</Text>

      {/* Date filter */}
      <View style={styles.filterContainer}>
        <View style={styles.presetRow}>
          {PRESETS.map((p) => (
            <TouchableOpacity
              key={p.key}
              style={[styles.presetBtn, preset === p.key && styles.presetBtnActive]}
              onPress={() => {
                setPreset(p.key);
                setCustomFrom(null);
                setCustomTo(null);
              }}
              testID={`commissions.preset-${p.key}`}
            >
              <Text style={[styles.presetText, preset === p.key && styles.presetTextActive]}>
                {p.label}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={[styles.presetBtn, preset === 'custom' && styles.presetBtnActive]}
            onPress={() => setPreset('custom')}
            testID="commissions.preset-custom"
          >
            <Text style={[styles.presetText, preset === 'custom' && styles.presetTextActive]}>
              Personalizado
            </Text>
          </TouchableOpacity>
        </View>

        {preset === 'custom' ? (
          <View style={styles.customDateRow}>
            <View style={styles.customDateField}>
              <Text style={styles.customDateLabel}>Desde</Text>
              <InventoryIsoDateField
                value={customFrom ? formatIsoDate(customFrom) : ''}
                onChange={(ymd) => {
                  if (!ymd) {
                    setCustomFrom(null);
                    return;
                  }
                  const d = parseIsoDate(ymd);
                  d.setHours(0, 0, 0, 0);
                  setCustomFrom(d);
                }}
                emptyLabel="Elegí fecha"
                ariaLabel="Fecha desde"
                showClearButton={false}
                iosEmptyBehavior="openPicker"
              />
            </View>
            <View style={styles.customDateField}>
              <Text style={styles.customDateLabel}>Hasta</Text>
              <InventoryIsoDateField
                value={customTo ? formatIsoDate(customTo) : ''}
                onChange={(ymd) => {
                  if (!ymd) {
                    setCustomTo(null);
                    return;
                  }
                  const d = parseIsoDate(ymd);
                  d.setHours(23, 59, 59, 999);
                  setCustomTo(d);
                }}
                emptyLabel="Elegí fecha"
                ariaLabel="Fecha hasta"
                showClearButton={false}
                iosEmptyBehavior="openPicker"
              />
            </View>
            <TouchableOpacity style={styles.applyBtn} onPress={() => void load()} testID="commissions.apply">
              <Text style={styles.applyBtnText}>Aplicar</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      <View style={styles.totalCard}>
        <Text style={styles.totalLabel}>Total a pagar en comisiones</Text>
        <Text style={styles.totalAmount}>{formatMoney(grandTotal)}</Text>
      </View>

      {/* Export + employee filter */}
      {summaries.length > 0 ? (
        <View style={styles.toolbarRow}>
          <TouchableOpacity style={styles.exportBtn} onPress={() => void handleExportCsv()} testID="commissions.export">
            <Text style={styles.exportBtnText}>Exportar CSV</Text>
          </TouchableOpacity>
          {employeeFilter ? (
            <TouchableOpacity
              style={styles.clearFilterBtn}
              onPress={() => setEmployeeFilter(null)}
              testID="commissions.clear-filter"
            >
              <Text style={styles.clearFilterText}>Ver todos</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {summaries.length === 0 ? (
        <Text style={styles.empty}>No hay comisiones registradas en este período.</Text>
      ) : (
        filteredSummaries.map((s) => (
          <View key={s.employeeId} style={styles.employeeCard}>
            <TouchableOpacity
              style={styles.employeeHeader}
              onPress={() => void handleToggleExpand(s.employeeId)}
              testID="commissions.employee-header"
              accessibilityRole="button"
              accessibilityLabel={`Comisiones de ${s.employeeName}`}
            >
              <View style={styles.employeeInfo}>
                <Text style={styles.employeeName}>{s.employeeName}</Text>
                <Text style={styles.employeeStats}>
                  {s.taskCount} {plural(s.taskCount, 'tarea', 'tareas')} · Ventas: {formatMoney(s.totalSales)}
                </Text>
              </View>
              <View style={styles.employeeCommission}>
                <Text style={styles.commissionAmount}>{formatMoney(s.totalCommission)}</Text>
                <Text style={styles.expandHint} testID="commissions.expand-hint">
                  {expandedEmployee === s.employeeId ? '▼' : '▶'}
                </Text>
              </View>
            </TouchableOpacity>
            {!employeeFilter ? (
              <TouchableOpacity
                onPress={() => setEmployeeFilter(s.employeeId)}
                testID={`commissions.filter-${s.employeeId}`}
                style={{ paddingHorizontal: 12, paddingBottom: 8 }}
              >
                <Text style={styles.filterLink}>Solo este empleado</Text>
              </TouchableOpacity>
            ) : null}

            {expandedEmployee === s.employeeId ? (
              <View style={styles.detailsContainer}>
                {loadingDetails ? (
                  <ActivityIndicator size="small" color="#2563eb" />
                ) : details.length === 0 ? (
                  <Text style={styles.emptyDetail}>Sin tareas con comisión</Text>
                ) : (
                  details.map((d) => (
                    <View key={d.taskId} style={styles.detailRow}>
                      <View style={styles.detailLeft}>
                        <Text style={styles.detailProduct} testID="commissions.detail-product">
                          {d.productName || 'Sin nombre'}
                        </Text>
                        <Text style={styles.detailMeta}>
                          {formatDate(d.createdAt)} · OT {d.workOrderId.slice(0, 8).toUpperCase()}
                        </Text>
                        <View style={styles.statusBadgeRow}>
                          <View style={styles.statusBadge}>
                            <Text style={styles.statusBadgeText}>
                              {WORK_ORDER_STATUS_LABELS[d.workOrderStatus as keyof typeof WORK_ORDER_STATUS_LABELS] ?? d.workOrderStatus}
                            </Text>
                          </View>
                        </View>
                        <Text style={styles.detailMeta}>
                          {d.quantity} × {formatMoney(d.unitPrice)} = {formatMoney(d.lineTotal)}
                        </Text>
                      </View>
                      <View style={styles.detailRight}>
                        {d.commissionPercent != null ? (
                          <Text style={styles.detailPercent}>{d.commissionPercent}%</Text>
                        ) : null}
                        <Text style={styles.detailCommission}>
                          {formatMoney(d.commissionAmount)}
                        </Text>
                      </View>
                    </View>
                  ))
                )}
              </View>
            ) : null}
          </View>
        ))
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  content: {
    padding: 16,
    paddingBottom: 32,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 16,
  },
  filterContainer: {
    marginBottom: 16,
  },
  presetRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  presetBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: '#e5e7eb',
  },
  presetBtnActive: {
    backgroundColor: '#2563eb',
  },
  presetText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  presetTextActive: {
    color: '#fff',
  },
  customDateRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  customDateField: {
    flex: 1,
    minWidth: 120,
  },
  customDateLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: 4,
  },
  applyBtn: {
    backgroundColor: '#2563eb',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  applyBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  totalCard: {
    backgroundColor: '#2563eb',
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    alignItems: 'center',
  },
  totalLabel: {
    color: '#bfdbfe',
    fontSize: 13,
    marginBottom: 4,
  },
  totalAmount: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
  },
  empty: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 14,
    paddingVertical: 24,
  },
  employeeCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  employeeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
  },
  employeeInfo: {
    flex: 1,
  },
  employeeName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  employeeStats: {
    fontSize: 12,
    color: '#6b7280',
  },
  employeeCommission: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  commissionAmount: {
    fontSize: 16,
    fontWeight: '700',
    color: '#16a34a',
  },
  expandHint: {
    fontSize: 12,
    color: '#9ca3af',
    marginLeft: 4,
  },
  detailsContainer: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    padding: 12,
  },
  emptyDetail: {
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: 13,
    paddingVertical: 8,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  detailLeft: {
    flex: 1,
  },
  detailProduct: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
    marginBottom: 2,
  },
  detailMeta: {
    fontSize: 12,
    color: '#6b7280',
  },
  detailRight: {
    alignItems: 'flex-end',
  },
  detailPercent: {
    fontSize: 12,
    color: '#7c3aed',
    fontWeight: '600',
  },
  detailCommission: {
    fontSize: 14,
    color: '#16a34a',
    fontWeight: '700',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
    marginBottom: 12,
    textAlign: 'center',
  },
  retryButton: {
    backgroundColor: '#2563eb',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  retryText: {
    color: '#fff',
    fontWeight: '600',
  },
  toolbarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  exportBtn: {
    backgroundColor: '#059669',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  exportBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  clearFilterBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb',
  },
  clearFilterText: {
    color: '#374151',
    fontSize: 13,
    fontWeight: '500',
  },
  filterLink: {
    fontSize: 12,
    color: '#2563eb',
    marginTop: 4,
    fontWeight: '500',
  },
  statusBadgeRow: {
    flexDirection: 'row',
    marginTop: 4,
  },
  statusBadge: {
    backgroundColor: '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 11,
    color: '#374151',
    fontWeight: '500',
  },
});
